// Parse MongoDB date format
export const toDate = (d) => {
  const maybe = d && (d.$date || d);
  return maybe ? new Date(maybe) : null;
};

// Get the most recent active plan
export const getLatestPlan = (plans) => {
  const activePlans = plans.filter((p) => p?.isActive);
  const source = activePlans.length ? activePlans : plans;
  return (
    source.sort((a, b) => {
      const dateA = toDate(b?.createdAt) || new Date(0);
      const dateB = toDate(a?.createdAt) || new Date(0);
      return dateA - dateB;
    })[0] || null
  );
};

// Get included credits from plan history
export const getIncludedCredits = (latest, purchased) => {
  const history = latest?.creditsHistory || [];
  const extractId = (h) => (typeof h === "string" ? h : h?.$oid || h?._id);
  const latestId = history.map(extractId).filter(Boolean).pop();
  if (!latestId) return 0;
  const match = purchased.find(
    (pc) => (pc?._id?.$oid || pc?._id || pc?.id) === latestId
  );
  return match?.credits || 0;
};

export const parseOptions = (pricingData, plan) => {
  if (!pricingData) return [];

  const { hasLifetime, activePlans, duration } = plan;
  const latest = getLatestPlan(activePlans || []);

  if (hasLifetime) {
    const rawOptions = pricingData["AI Credits for Lifetime Deals"] || [];
    let tierIndex = 0;
    const planName = latest?.planName || latest?.planType || "";
    if (planName.includes("Tier 3") || planName.includes("3 Site")) tierIndex = 1;
    else if (planName.includes("Tier 4") || planName.includes("10 Site")) tierIndex = 2;

    return rawOptions
      // Hide rows meant only for the public pricing page (e.g. the free $0 100-credit row)
      .filter((opt) => opt.Audience !== "Pricing only")
      .map((opt) => {
        const priceIds = [
          opt.priceIdForLTDTier1,
          opt.priceIdForLTDTier3,
          opt.priceIdForLTDTier4,
        ];
        const priceId = priceIds[tierIndex] || opt.priceIdForLTDTier1;
        const stripeDetail = opt.stripeDetails?.[priceId] || opt._stripeDetails?.[priceId] || {};

        return {
          priceId,
          credits: Number(opt.Credits || opt.credits),
          label: opt["Credits Dropdown"],
          price: stripeDetail.unitAmount ? stripeDetail.unitAmount / 100 : undefined,
          currency: stripeDetail.currency || opt["Currency Name"] || "USD",
          currencySymbol: opt["Currency Symbol"],
        };
      })
      .filter((o) => o.priceId && o.credits)
      .sort((a, b) => a.credits - b.credits);
  } else {
    const rawOptions = pricingData["AI Credits for Recurring Plans"] || [];
    const recurringPlans = pricingData["Recurring Plans"] || [];
    const isYearly = duration === "yearly" || latest?.duration === "yearly";

    let basePriceId = null;
    let baseStripeDetail = {};
    const currentBasePlan = recurringPlans.find(
      (p) => p["Plan name"] === latest?.planName || p["Plan name"] === latest?.planType
    );

    if (currentBasePlan) {
      basePriceId = isYearly ? currentBasePlan.yearlyPriceId : currentBasePlan.monthlyPriceId;
      baseStripeDetail = currentBasePlan.stripeDetails?.[basePriceId] || currentBasePlan._stripeDetails?.[basePriceId] || {};
    }

    return rawOptions
      // Hide rows meant only for the public pricing page (e.g. the free $0 100-credit row)
      .filter((opt) => opt.Audience !== "Pricing only")
      .map((opt) => {
        const priceId = isYearly ? opt.priceIdYearly : opt.priceIdMonthly;
        const stripeDetail = opt.stripeDetails?.[priceId] || opt._stripeDetails?.[priceId] || {};
        const creditPrice = stripeDetail.unitAmount ? stripeDetail.unitAmount / 100 : 0;

        let label = opt["Credits Dropdown"];
        if (isYearly && label) {
          label = label.replace("/ month", "/ year");
        }

        let basePrice = 0;
        if (basePriceId && currentBasePlan) {
          basePrice = baseStripeDetail.unitAmount ? baseStripeDetail.unitAmount / 100 : 0;
        }

        return {
          priceId,
          basePriceId,
          credits: Number(opt.credits || opt.Credits),
          label,
          price: basePrice + creditPrice,
          currency: baseStripeDetail.currency || stripeDetail.currency || opt["Currency Name"] || currentBasePlan?.["Currency Name"] || "USD",
          currencySymbol: currentBasePlan?.["Currency Symbol"] || opt["Currency Symbol"],
        };
      })
      .filter((o) => o.priceId && o.credits)
      .sort((a, b) => a.credits - b.credits);
  }
};

export const calculateResetDays = (nextBillingDate) => {
  const nextReset = toDate(nextBillingDate);
  if (!nextReset) return 10;
  return Math.max(0, Math.ceil((nextReset - new Date()) / 86400000));
};

export const cleanUrlParams = () => {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  if (params.has("session_id") || params.has("cancelled")) {
    window.history.replaceState({}, "", window.location.pathname);
  }
};

export const getButtonText = (plan) => {
  if (!plan.hasPlan) return "Purchase Plan";
  if (["Portfolio", "Individual"].includes(plan.type)) return "Upgrade Plan";
  if (plan.hasLifetime) return "Add Credits";
  return "Add More Credits";
};
